import type {
  IgLookupErrorResponse,
  IgLookupRejectedAccount,
} from "../../../../../api/post";

/** A form account, as far as naming a refused one needs. */
export interface LookupFormAccount {
  _id?: string;
  login?: string;
  displayLogin?: string;
}

export const rejectedList = (value: unknown): IgLookupRejectedAccount[] =>
  Array.isArray(value)
    ? value
        .filter(
          (entry): entry is IgLookupRejectedAccount =>
            typeof entry?.accountId === "string"
        )
        .map((entry) => ({
          ...entry,
          // An unknown reason falls back to asking for a reconnect, the safer prompt.
          reason:
            entry.reason === "no_credentials"
              ? "no_credentials"
              : "token_rejected",
        }))
    : [];

export const serverRejectedAccounts = (
  error: unknown
): IgLookupRejectedAccount[] =>
  rejectedList(
    (
      error as {
        response?: { data?: Partial<IgLookupErrorResponse> };
      }
    )?.response?.data?.rejectedAccounts
  );

const serverStatus = (error: unknown): number | undefined =>
  (error as { response?: { status?: number } })?.response?.status;

export const refusedEveryAccount = (
  error: unknown,
  requested: string[],
  refused: IgLookupRejectedAccount[]
) =>
  serverStatus(error) === 403 &&
  requested.length > 0 &&
  requested.every((id) => refused.some((entry) => entry.accountId === id));

/**
 * `@login` for each account Instagram refused. The server sends the login when it
 * has one; otherwise it comes from the accounts on the form.
 */
export function rejectedAccountHandles(
  rejected: IgLookupRejectedAccount[],
  formAccounts: LookupFormAccount[]
): string[] {
  return rejected.map((entry) => {
    const account = formAccounts.find(({ _id }) => _id === entry.accountId);
    const login =
      entry.login?.trim() || account?.displayLogin || account?.login;
    return login ? `@${login.replace(/^@/, "")}` : "an account";
  });
}
